/**
 * SigTDD Contact Page
 *
 * Contact form for user inquiries, support requests, and feedback.
 */

import { useState } from 'react';
import './ContactPage.css';

// Icons as inline SVGs
const MessageIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

interface ContactPageProps {
  onClose?: () => void;
}

export default function ContactPage({ onClose }: ContactPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      company: formData.get('company'),
      subject: formData.get('subject'),
      message: formData.get('message'),
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to send message');
      }

      setSubmitted(true);
    } catch {
      setError('The message could not be sent. Try again, or email admin@sigtdd.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <main className="contact-page">
        <div className="contact-container">
          <div className="contact-success">
            <div className="contact-success-icon">
              <CheckIcon />
            </div>
            <h1>Message sent</h1>
            <p>
              Your message has been delivered to admin@sigtdd.com.
            </p>
            {onClose && (
              <button className="contact-back-btn" onClick={onClose}>
                <ArrowLeftIcon />
                Back to App
              </button>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="contact-page">
      <div className="contact-container">
        {/* Header */}
        <header className="contact-header">
          <div className="contact-icon-wrapper">
            <div className="contact-icon-glow" />
            <div className="contact-icon">
              <MessageIcon />
            </div>
          </div>
          <h1>Contact SigTDD</h1>
          <p className="contact-email-direct">
            <a href="mailto:admin@sigtdd.com">admin@sigtdd.com</a>
          </p>
          <p className="contact-intro">
            Support, pricing, feature requests and bug reports for the time-distance diagram tool all
            go to the same address. Use the form or write directly.
          </p>

          {error && (
            <div className="contact-error">
              {error}
            </div>
          )}
        </header>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="contact-form-row">
            <div className="contact-field">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="First name"
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div className="contact-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="name@example.com"
              required
            />
          </div>

          <div className="contact-field">
            <label htmlFor="company">Company / Organisation (Optional)</label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder="Your company or council name"
            />
          </div>

          <div className="contact-field">
            <label htmlFor="subject">Subject</label>
            <select id="subject" name="subject" required defaultValue="">
              <option value="" disabled>Select a subject</option>
              <option value="general">General Inquiry</option>
              <option value="sales">Sales &amp; Pricing</option>
              <option value="support">Technical Support</option>
              <option value="feature">Feature Request</option>
              <option value="bug">Bug Report</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="contact-field">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              placeholder="Your message"
              rows={5}
              required
            />
          </div>

          <button
            type="submit"
            className="contact-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              'Sending...'
            ) : (
              <>
                <SendIcon />
                Send Message
              </>
            )}
          </button>
        </form>

        {/* Back link */}
        {onClose && (
          <div className="contact-back">
            <button className="contact-back-link" onClick={onClose}>
              <ArrowLeftIcon />
              Back to App
            </button>
          </div>
        )}

      </div>
    </main>
  );
}
